import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { PermissionsService } from '../../user/permissions/permissions.service';
import { PermissionsType } from '../../user/permissions/permissions.datamodel';
import { SectionEnum } from '../section/section';
import { AppService } from '../../application/app.service';
import { CoreService } from './core.service';
import { UserService } from '../../user/user.service';
import { EntityType } from '../../shared/list/lssEntity.datamodel';

import * as CoreActions from './core.actions';
import * as PermissionsActions from '../../user/permissions/permissions.actions';

@Injectable()
export class CoreEffects {
  /**
   * When the user logs in, select the first ACL if the user has VIEW INVENTORY permission,
   * otherwise select the user's organization
   */
  entitySelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PermissionsActions.RetrievedTaAdminOffices
      ),
      mergeMap(() => {
        return of(CoreActions.EntitySelected({
          entity: {
            label: this.permissionsService.isUserAuthorized(PermissionsType.VIEW_INVENTORY)
              && this.permissionsService.getPermissionsACLs(PermissionsType.VIEW_INVENTORY).length ?
              this.permissionsService.getPermissionsACLs(PermissionsType.VIEW_INVENTORY)[0].actorName :
              this.userService.getUser()?.organizationId,
            type: EntityType.ORGANISATION
          }
        }));
      })
    )
  );

  /**
   * After selecting a new ORG, if the user has VIEW INVENTORY permission, navigate to the Inventory section,
   * otherwise navigate to the MyApps section
   */
  navigateOnUserLogIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CoreActions.EntitySelected
      ),
      withLatestFrom(this.appService.entitySelected$),
      mergeMap(([_, entity]) => {
        if (entity) {
          return of(CoreActions.Navigate({
            section: this.permissionsService.isUserAuthorized(PermissionsType.VIEW_INVENTORY, entity.label)
              ? SectionEnum.INVENTORY.valueOf() : SectionEnum.MYAPPS.valueOf(),
            isRestoredState: false
          }));
        }
        return EMPTY;
      })
    )
  );

  /**
   * After selecting a new ORG, if the user has VIEW INVENTORY permission, load the inventory content,
   * otherwise load the user content
   */
  loadInventoryContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CoreActions.EntitySelected
      ),
      withLatestFrom(this.appService.entitySelected$),
      mergeMap(([_, entity]) => {
        if (entity) {
          this.permissionsService.isUserAuthorized(PermissionsType.VIEW_INVENTORY, entity.label) ?
            this.coreService.getInventoryContent(entity) : this.coreService.loadUserContent(
              this.userService.getUser(),
              this.permissionsService.isUserAuthorized(PermissionsType.MANAGE_SCRIPT_CONFIGURATION),
              this.permissionsService.isUserAuthorized(PermissionsType.ACCESS_MYAMADEUS));
        }
        return of(CoreActions.LoadingInventoryContent());
      }))
  );

  constructor(
    private readonly actions$: Actions,
    private readonly coreService: CoreService,
    private readonly permissionsService: PermissionsService,
    private readonly userService: UserService,
    private readonly appService: AppService
  ) {
  }
}
